import { useState } from "react";
import api, { apiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { TYPE_META } from "@/lib/format";
import {
  BUSINESS_TYPES, businessTypeLabel, GOAL_OPTIONS, TYPE_QUESTIONS, COMM_OPTIONS, ONB_NETWORKS, TYPE_ONB, GOAL_ONB,
  needsLocalClientNote, LOCAL_CLIENT_NOTE, LOCAL_RECOMMENDER_GOAL,
} from "@/lib/onboardingConfig";
import { MarketField } from "@/components/onboarding/MarketField";
import { autoAvatarName } from "@/components/Onboarding";
import { Plus, Trash2, Pencil, Loader2, Sparkles } from "lucide-react";
import "@/styles/onboarding.css";

export const inputCls =
  "w-full rounded-xl border border-brand-line bg-brand-panel px-3.5 py-2.5 text-sm text-brand-ink outline-none transition-all duration-150 focus:border-brand-accent focus:ring-4 focus:ring-brand-accent/15";
const chipCls = (on) => `rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors duration-150 ${on ? "border-brand-accent bg-brand-accent/[0.12] text-brand-accent" : "border-brand-line bg-brand-panel text-brand-ink hover:border-brand-accent/50"}`;

export const Section = ({ letter, title, desc, kind, children, testId }) => (
  <section className="ca-card animate-fade-up space-y-5 p-6" data-testid={testId}>
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="flex items-start gap-3">
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg border border-brand-accent/40 bg-brand-accent/[0.08] font-mono text-xs font-bold text-brand-accent">{letter}</span>
        <div>
          <h2 className="ca-h2">{title}</h2>
          {desc && <p className="mt-0.5 text-sm text-brand-muted">{desc}</p>}
        </div>
      </div>
      {kind && (
        <span data-testid={`${testId}-kind`} className={`rounded-full border px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider ${kind === "negocio" ? "border-brand-accent/40 text-brand-accent" : "border-brand-line text-brand-muted"}`}>
          {kind === "negocio" ? "Conocimiento del negocio" : "Configuración de prospección"}
        </span>
      )}
    </div>
    {children}
  </section>
);

export const Field = ({ label, hint, error, children }) => (
  <div>
    <label className="mb-1.5 block text-sm font-semibold text-brand-ink">{label}</label>
    {children}
    {hint && !error && <p className="mt-1 text-xs text-brand-muted">{hint}</p>}
    {error && <p className="mt-1 text-xs font-medium text-brand-neg">{error}</p>}
  </div>
);

const Text = ({ form, k, set, rows, ph, error }) => rows ? (
  <textarea data-testid={`brain-${k}`} className={`${inputCls} ${error ? "border-brand-neg" : ""}`} rows={rows} value={form[k] || ""} onChange={(e) => set(k, e.target.value)} placeholder={ph} />
) : (
  <input data-testid={`brain-${k}`} className={`${inputCls} ${error ? "border-brand-neg" : ""}`} value={form[k] || ""} onChange={(e) => set(k, e.target.value)} placeholder={ph} />
);

export const InfoBlock = ({ form, set, setQ, patch, errors }) => (
  <Section letter="A" title="Información del negocio" desc="Quién eres y dónde trabajas." kind="negocio" testId="brain-block-a">
    <Field label="Nombre del negocio"><Text form={form} k="business_name" set={set} ph="Cómo se llama lo tuyo." /></Field>
    <Field label="Tipo de actividad">
      <select data-testid="brain-business-type" className={`${inputCls} appearance-none`} value={form.business_type || ""} onChange={(e) => set("business_type", e.target.value)}>
        <option value="">Sin definir</option>
        {BUSINESS_TYPES.map((t) => <option key={t.k} value={t.k}>{TYPE_ONB[t.k]?.[0] || t.label}</option>)}
      </select>
    </Field>
    {form.business_type === "otro" && (
      <Field label="Describe tu actividad">
        <input data-testid="brain-q-other_activity" className={inputCls} value={(form.questionnaire || {}).other_activity || ""} onChange={(e) => setQ("other_activity", e.target.value)} />
      </Field>
    )}
    {(TYPE_QUESTIONS[form.business_type] || []).length > 0 && (
      <div className="space-y-4 rounded-xl border border-brand-line bg-brand-panel2/40 p-4" data-testid="brain-questionnaire">
        <p className="text-sm font-semibold text-brand-ink">Sobre tu actividad · {TYPE_ONB[form.business_type]?.[0] || businessTypeLabel(form.business_type)}</p>
        {(TYPE_QUESTIONS[form.business_type] || []).map((q) => {
          const v = (form.questionnaire || {})[q.k] || "";
          return (
            <Field key={q.k} label={<>{q.label}{!q.required && <span className="ml-1.5 text-xs font-normal text-brand-muted">(opcional)</span>}</>}>
              {q.type === "textarea" ? <textarea data-testid={`brain-q-${q.k}`} className={inputCls} rows={2} value={v} onChange={(e) => setQ(q.k, e.target.value)} />
                : q.type === "select" ? (
                  <select data-testid={`brain-q-${q.k}`} className={`${inputCls} appearance-none`} value={v} onChange={(e) => setQ(q.k, e.target.value)}>
                    <option value="">Selecciona…</option>
                    {q.options.map((o) => <option key={o.k} value={o.k}>{o.label}</option>)}
                  </select>
                ) : <input data-testid={`brain-q-${q.k}`} className={inputCls} value={v} onChange={(e) => setQ(q.k, e.target.value)} />}
            </Field>
          );
        })}
      </div>
    )}
    <Field label="Mercado principal" error={errors.market}>
      <div data-testid="brain-market"><MarketField biz={form} onPatch={patch} error={errors.market} testPrefix="brain" /></div>
    </Field>
  </Section>
);

export const OfferBlock = ({ form, set, errors }) => (
  <Section letter="B" title="Oferta" desc="Qué vendes, a quién y qué problema resuelves." kind="negocio" testId="brain-block-b">
    <Field label="¿Qué ofreces?" error={errors.offer}><Text form={form} k="offer" set={set} rows={2} ph="Tu producto o servicio en una o dos frases." error={errors.offer} /></Field>
    <Field label="Productos y precios"><Text form={form} k="products_prices" set={set} rows={2} ph="Qué vendes y a qué precio." /></Field>
    <Field label="Oferta prioritaria" hint="Si tienes varias, por dónde empezar."><Text form={form} k="priority_offers" set={set} ph="Ej.: programa de mentoría trimestral" /></Field>
    <Field label="¿Qué problema le resuelves?"><Text form={form} k="problem" set={set} rows={2} ph="El dolor concreto que le quitas." /></Field>
    <Field label="Cliente / público ideal" error={errors.ideal_client}><Text form={form} k="ideal_client" set={set} rows={2} ph="A quién ayudas o a quién sueles vender." error={errors.ideal_client} /></Field>
  </Section>
);

export const GoalsBlock = ({ form, set, toggleGoal }) => (
  <Section letter="C" title="Objetivos" desc="Qué quieres conseguir ahora. Cambiar objetivos no invalida el resumen aprobado." kind="prospeccion" testId="brain-block-c">
    <div className="flex flex-wrap gap-2" data-testid="brain-goals">
      {GOAL_OPTIONS.filter((g) => GOAL_ONB[g.k] || (form.goals_list || []).includes(g.k)).map((g) => (
        <button key={g.k} type="button" data-testid={`brain-goal-${g.k}`} onClick={() => toggleGoal(g.k)} className={chipCls((form.goals_list || []).includes(g.k))}>
          {GOAL_ONB[g.k]?.[0] || g.label}
        </button>
      ))}
    </div>
    {needsLocalClientNote(form) && (
      <div className="rounded-md border-l-2 border-brand-accent/50 bg-brand-accent/[0.04] px-3 py-2 text-xs text-brand-muted" data-testid="brain-local-client-note">
        <p>{LOCAL_CLIENT_NOTE}</p>
        {!(form.goals_list || []).includes(LOCAL_RECOMMENDER_GOAL) && (
          <button type="button" onClick={() => toggleGoal(LOCAL_RECOMMENDER_GOAL)} data-testid="brain-add-recommenders" className={`mt-2 ${chipCls(false)}`}>
            ＋ Añadir «{GOAL_ONB[LOCAL_RECOMMENDER_GOAL][0]}»
          </button>
        )}
      </div>
    )}
    {(form.goals_list || []).includes("reactivar") && (
      <p className="rounded-md border-l-2 border-brand-accent/50 bg-brand-accent/[0.04] px-3 py-2 text-xs text-brand-muted" data-testid="brain-reactivar-note">
        «Retomar contactos que ya conoces» se trabaja con personas de tu red; no genera perfiles de búsqueda.
      </p>
    )}
    <Field label="Describe tu objetivo con tus palabras" hint="Este texto forma parte del conocimiento del negocio y del resumen."><Text form={form} k="goals" set={set} rows={2} ph="Ej.: 5 reuniones al mes." /></Field>
  </Section>
);

const emptyAv = { name: "", type: "cliente", related_offer: "", entity: "persona", sector: "", location: "", need: "", fit_signals: "", discard_signals: "", channels: "instagram", description: "" };

export const ProfilesBlock = ({ form, setForm, applyBrain }) => {
  const [av, setAv] = useState(emptyAv);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const list = form.avatars_list || [];
  const searchGoals = (form.goals_list || []).filter((g) => g !== "reactivar");
  const up = (k, v) => setAv((f) => ({ ...f, [k]: v }));
  const save = () => {
    setForm((f) => {
      const l = f.avatars_list || [];
      if (editId) return { ...f, avatars_list: l.map((a) => (a.id === editId ? { ...a, ...av, name: autoAvatarName(av) } : a)) };
      return { ...f, avatars_list: [...l, { ...av, id: "a" + Date.now(), name: autoAvatarName(av) }] };
    });
    setAv(emptyAv); setEditId(null);
  };
  const remove = (id) => { setForm((f) => ({ ...f, avatars_list: (f.avatars_list || []).filter((a) => a.id !== id) })); if (editId === id) { setAv(emptyAv); setEditId(null); } };
  const suggest = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.post("/business-brain/avatars/suggest", { objective: "" });
      if (r.data.status === "generated" || r.data.status === "exists") { applyBrain({ avatars_list: r.data.avatars && r.data.status === "exists" ? r.data.avatars : [...list, ...(r.data.avatars || [])] }); toast.success("Perfiles propuestos. Revísalos antes de buscar."); }
      else toast.info((r.data.needs_information?.questions || [])[0] || "Necesitamos un poco más de información de tu negocio.");
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  return (
    <Section letter="D" title="Perfiles que quieres encontrar" desc="Tipos de persona o empresa a los que te diriges. No necesitan nombre: se etiquetan solos." kind="prospeccion" testId="brain-block-d">
      {list.length === 0 ? (
        <div className="rounded-xl border border-dashed border-brand-line px-4 py-6 text-center" data-testid="brain-avatars-empty">
          <p className="text-sm text-brand-muted">Aún no hay perfiles. {searchGoals.length ? "Puedes pedir a CaptAcción que los proponga o crearlos abajo." : "Crea el primero abajo."}</p>
          {searchGoals.length > 0 && (
            <button type="button" onClick={suggest} disabled={busy} data-testid="brain-suggest-avatars" className="mt-3 inline-flex items-center gap-2 rounded-md bg-brand-accent px-3 py-2 text-xs font-bold text-brand-canvas transition-all hover:brightness-110 disabled:opacity-60">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Proponer perfiles
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-2" data-testid="brain-avatars-list">
          {list.map((a) => (
            <div key={a.id} data-testid={`brain-avatar-${a.id}`} className="flex items-start gap-3 rounded-xl border border-brand-line bg-brand-panel p-3">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-brand-ink">{a.name}</p>
                <p className="text-xs text-brand-muted">{TYPE_META[a.type]?.label}{a.sector ? ` · ${a.sector}` : ""}{a.location ? ` · ${a.location}` : ""}</p>
                {a.need && <p className="mt-1 text-xs text-brand-muted">Necesidad: {a.need}</p>}
                {(a.fit_signals || a.discard_signals) && <p className="mt-0.5 text-xs text-brand-muted">{a.fit_signals ? `Encaja: ${a.fit_signals}` : ""}{a.fit_signals && a.discard_signals ? " · " : ""}{a.discard_signals ? `Descarta: ${a.discard_signals}` : ""}</p>}
              </div>
              <button type="button" onClick={() => { setAv({ ...emptyAv, ...a }); setEditId(a.id); }} data-testid={`brain-avatar-edit-${a.id}`} className="shrink-0 text-brand-muted transition-colors hover:text-brand-accent" title="Editar perfil"><Pencil size={15} /></button>
              <button type="button" onClick={() => remove(a.id)} data-testid={`brain-avatar-remove-${a.id}`} className="shrink-0 text-brand-muted transition-colors hover:text-brand-neg" title="Quitar perfil"><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
      )}
      <div className="rounded-xl border border-brand-line bg-brand-panel2/40 p-4" data-testid="brain-avatar-form">
        <p className="mb-3 text-sm font-bold text-brand-ink">{editId ? "Editar perfil" : "Nuevo perfil"}</p>
        <div className="mb-3 flex flex-wrap gap-2">
          {Object.entries(TYPE_META).map(([k, v]) => <button key={k} type="button" data-testid={`brain-av-type-${k}`} onClick={() => up("type", k)} className={chipCls(av.type === k)}>{v.label}</button>)}
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <select data-testid="brain-av-entity" className={`${inputCls} appearance-none`} value={av.entity} onChange={(e) => up("entity", e.target.value)}><option value="persona">Persona</option><option value="empresa">Empresa / negocio</option></select>
          <input data-testid="brain-av-sector" className={inputCls} value={av.sector} onChange={(e) => up("sector", e.target.value)} placeholder="Sector o actividad" />
          <input className={inputCls} value={av.related_offer} onChange={(e) => up("related_offer", e.target.value)} placeholder="Oferta relacionada" />
          <input className={inputCls} value={av.location} onChange={(e) => up("location", e.target.value)} placeholder="Ubicación (vacío = mercado general)" />
        </div>
        <input className={`${inputCls} mt-3`} value={av.need} onChange={(e) => up("need", e.target.value)} placeholder="Necesidad o interés" />
        <input className={`${inputCls} mt-3`} value={av.fit_signals} onChange={(e) => up("fit_signals", e.target.value)} placeholder="Señales de buen encaje" />
        <input className={`${inputCls} mt-3`} value={av.discard_signals} onChange={(e) => up("discard_signals", e.target.value)} placeholder="Perfiles que prefieres excluir" />
        <div className="mt-3 flex gap-2">
          <button type="button" onClick={save} data-testid="brain-add-avatar" className="inline-flex items-center gap-2 rounded-md border border-brand-line bg-brand-panel px-3 py-2 text-xs font-semibold text-brand-ink transition-all hover:border-brand-accent hover:text-brand-accent">
            <Plus size={14} /> {editId ? "Guardar perfil" : "Añadir perfil"}
          </button>
          {editId && <button type="button" onClick={() => { setAv(emptyAv); setEditId(null); }} className="rounded-md border border-brand-line bg-brand-panel px-3 py-2 text-xs font-semibold text-brand-ink">Cancelar</button>}
        </div>
      </div>
    </Section>
  );
};

const LEGACY_COMM = { mensajes: "Mensajes escritos", audios: "Audios", llamadas: "Llamadas" };

export const OperativeBlock = ({ form, set, toggleArr }) => {
  const nets = form.networks || [];
  const realNets = nets.filter((k) => k !== "sin_presencia");
  return (
    <Section letter="E" title="Operativa y preferencias" desc="Cómo trabajas hoy. Ajusta las recomendaciones sin tocar el resumen." kind="prospeccion" testId="brain-block-e">
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Tu experiencia prospectando">
          <select data-testid="brain-experience" className={`${inputCls} appearance-none`} value={form.experience_level || ""} onChange={(e) => set("experience_level", e.target.value)}>
            <option value="principiante">Estoy empezando</option>
            <option value="con_perfiles">Ya lo he probado algunas veces</option>
            <option value="avanzado">Lo hago con frecuencia</option>
          </select>
        </Field>
        <Field label="Cómo prefieres comunicarte">
          <select data-testid="brain-preference" className={`${inputCls} appearance-none`} value={form.message_preference || ""} onChange={(e) => set("message_preference", e.target.value)}>
            <option value="">Lo decidiré más adelante</option>
            {COMM_OPTIONS.map((c) => <option key={c.k} value={c.k}>{c.label}</option>)}
            {LEGACY_COMM[form.message_preference] && <option value={form.message_preference}>{LEGACY_COMM[form.message_preference]}</option>}
          </select>
        </Field>
      </div>
      <Field label="¿Cómo te presentas? (opcional)">
        <input data-testid="brain-self-intro" className={inputCls} maxLength={200} value={form.self_intro || ""}
          onChange={(e) => set("self_intro", e.target.value)}
          placeholder="Ej.: Soy Laura, fisio en Valencia · Llevo la parte de alianzas de Wake Up" />
        <p className="mt-1.5 text-xs text-brand-muted">Una frase corta, como lo dirías tú. La IA la usará solo cuando haga falta presentarse; muchas veces el primer mensaje funciona mejor sin presentación.</p>
      </Field>
      <Field label="Redes en las que tienes presencia">
        <div className="flex flex-wrap gap-2" data-testid="brain-networks">
          {ONB_NETWORKS.map((n) => <button key={n.k} type="button" data-testid={`brain-net-${n.k}`} onClick={() => toggleArr("networks", n.k)} className={chipCls(nets.includes(n.k))}>{n.label}</button>)}
        </div>
      </Field>
      {realNets.length > 0 && (
        <Field label="¿En cuáles empiezas desde cero?">
          <div className="flex flex-wrap gap-2">
            {realNets.map((k) => <button key={k} type="button" data-testid={`brain-scratch-${k}`} onClick={() => toggleArr("networks_from_scratch", k)} className={chipCls((form.networks_from_scratch || []).includes(k))}>{ONB_NETWORKS.find((n) => n.k === k)?.label || k}</button>)}
          </div>
        </Field>
      )}
      <Field label="¿Cómo consigues oportunidades actualmente?"><Text form={form} k="current_acquisition" set={set} ph="Ej.: Recomendaciones, Redes sociales" /></Field>
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Tiempo disponible para prospectar"><Text form={form} k="time_availability" set={set} ph="Ej.: 2–3 horas a la semana" /></Field>
        <Field label="Recursos que tienes"><Text form={form} k="resources" set={set} ph="Web, testimonios, casos…" /></Field>
      </div>
    </Section>
  );
};
